/**
 * Tela do Garçom — quadro Kanban dos pedidos do salão em tempo real.
 *
 * Colunas por status; arrastar entre colunas avança o status (transições do
 * garçom: confirmar/entregar/cancelar), dentro da coluna prioriza (persiste).
 * A aba "A pagar" lista contas entregues e não pagas.
 */
import { useCallback, useMemo, useState } from 'react'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import { ordersApi, waiterCallsApi, config, billsApi } from '../lib/api.js'
import type { OrderDetailed, BillDto } from '../lib/api.js'
import { getTokenPayload, getUserRoles, hasAnyRole } from '../lib/auth.js'
import { useRealtime } from '../lib/realtime.js'
import { useI18n } from '../lib/i18n.js'
import { OrdersKanban } from '../components/OrdersKanban.js'
import { OrderFormModal } from '../components/OrderFormModal.js'
import { TransferTableModal } from '../components/TransferTableModal.js'
import { PriceAdjustmentModal } from '../components/PriceAdjustmentModal.js'
import { WaiterTables } from '../components/WaiterTables.js'
import { WaiterTableClaims } from '../components/WaiterTableClaims.js'
import { isAdjustmentApprover } from '@self-restaurant/shared'
import { MANAGER_ROLES } from '../lib/routeRoles.js'
import type { KanbanColumn, KanbanStatus } from '../components/OrdersKanban.js'
import { Button, Empty, Spinner, toast } from '../components/ui/index.js'
import { cn, tenantCurrency, priceLocaleOf } from '../lib/utils.js'
import { formatInRestaurant } from '@self-restaurant/shared/src/utils/timezone'

// rótulos resolvidos no render via t() — o idioma muda sem recarregar
const STATUS_KEY: Record<OrderDetailed['status'], string> = {
  pending: 'admin.status.pending',
  confirmed: 'admin.status.confirmed',
  preparing: 'admin.status.preparing',
  ready: 'admin.status.ready',
  delivered: 'admin.status.delivered',
  cancelled: 'admin.status.cancelled',
  // fluxo garçom (0085) — nunca aparecem no kanban (visão por mesa)
  waiter_sent: 'admin.waiter.flow_sent',
  waiter_ack: 'admin.waiter.flow_ack',
  relayed: 'admin.waiter.flow_relayed',
}

/** Transições válidas do fluxo (espelho do service) — alvo por status. */
const NEXT_STATUS: Record<KanbanStatus, KanbanStatus[]> = {
  pending: ['confirmed', 'cancelled'],
  confirmed: ['preparing', 'cancelled'],
  preparing: ['ready'],
  ready: ['delivered'],
  delivered: [],
  cancelled: [],
  waiter_sent: ['waiter_ack', 'cancelled'],
  waiter_ack: ['relayed', 'cancelled'],
  relayed: [],
}
/** Garçom confirma, entrega e cancela; preparo é com a cozinha. */
const WAITER_TARGETS: KanbanStatus[] = ['confirmed', 'delivered', 'cancelled']

/** Status em que o pedido ainda pertence à mesa (espelho do service) —
 * transferência de mesa disponível nesses status, não pago. */
const TRANSFERABLE: OrderDetailed['status'][] = ['pending', 'confirmed', 'preparing', 'ready']

function elapsedLabel(createdAt: number): string {
  const sec = Math.max(0, Math.floor(Date.now() / 1000) - createdAt)
  const min = Math.floor(sec / 60)
  return min >= 60 ? `${Math.floor(min / 60)}h${min % 60}` : `${min} min`
}

function formatCents(cents: number, currency = 'BRL', locale = 'pt'): string {
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100)
}

export function Waiter() {
  const queryClient = useQueryClient()
  const { t, lang } = useI18n()
  const [formOpen, setFormOpen] = useState(false)
  const [editingOrder, setEditingOrder] = useState<OrderDetailed | null>(null)
  const [transferOrder, setTransferOrder] = useState<OrderDetailed | null>(null)
  // #274/#284: garçom pede ajuste; Gerente/Dono aplicam direto
  const [adjustOrder, setAdjustOrder] = useState<OrderDetailed | null>(null)
  const canApproveAdjustments = getUserRoles().some((r) => isAdjustmentApprover(r))
  // celular: uma coluna por vez em abas; desktop: quadro completo
  const [mobileTab, setMobileTab] = useState<KanbanStatus>('pending')
  // #132: "minhas mesas" — filtro do salão pelas claims do garçom logado
  const [mineOnly, setMineOnly] = useState(false)

  const { data: orders, isLoading } = useQuery({
    queryKey: ['orders', 'waiter', lang],
    queryFn: () => ordersApi.list({ limit: 100, locale: lang }),
    refetchInterval: 30_000,
  })
  // /admin/config exige role de gestor — garçom usa só o fallback
  const canReadConfig = hasAnyRole(getTokenPayload(), [
    'manager',
    'restaurant_owner',
    'platform_owner',
  ])
  const { data: cfg } = useQuery({
    queryKey: ['config'],
    queryFn: config.get,
    enabled: canReadConfig,
  })
  // #248: horários no fuso do RESTAURANTE (não do aparelho), para todo papel
  const { data: publicTz } = useQuery({
    queryKey: ['restaurant-timezone'],
    queryFn: config.timezone,
    enabled: !canReadConfig,
    staleTime: 10 * 60_000,
  })
  const restaurantTz = cfg?.timezone ?? publicTz ?? null
  // #419/#413: moeda do PAÍS do tenant (nunca deduzida do idioma). Garçom sem
  // papel de gestor lê do cardápio público (/menu/restaurant traz currency).
  const { data: publicSnapshot } = useQuery({
    queryKey: ['restaurant-country-currency'],
    queryFn: config.countryCurrency,
    enabled: !canReadConfig,
    staleTime: 10 * 60_000,
  })
  const currency = tenantCurrency(cfg, publicSnapshot?.currency)
  // #428: formatação preços/datas = língua base + PAÍS do restaurante
  // («12,50 €» em Portugal) — nunca gravado nem mostrado
  const intlLocale = priceLocaleOf(lang, publicSnapshot?.country, cfg)

  // Modo garçom (0085): gestor lê do /admin/config; garçom, do menu público.
  // 'waiter' = visão por mesa (pedidos enviados pelo cliente), sem kanban.
  const { data: publicFlow } = useQuery({
    queryKey: ['restaurant-order-flow'],
    queryFn: config.orderFlow,
    enabled: !canReadConfig,
    staleTime: 10 * 60_000,
  })
  const waiterMode = (cfg?.order_flow ?? publicFlow ?? 'full') === 'waiter'

  // Fila de chamadas do salão — persistida no banco: recarregar a página ou
  // reconectar o WebSocket não perde chamada nenhuma
  const { data: waiterCalls } = useQuery({
    queryKey: ['waiter-calls'],
    queryFn: waiterCallsApi.list,
    refetchInterval: 15_000,
  })
  const openCalls = useMemo(
    () =>
      (waiterCalls ?? [])
        .filter((c) => c.attended_at === null)
        .sort((a, b) => a.called_at - b.called_at),
    [waiterCalls],
  )

  // Contas enviadas pelo cliente ("fechar a conta") aguardando recebimento
  const { data: bills } = useQuery({
    queryKey: ['bills'],
    queryFn: () => billsApi.list('awaiting_payment'),
    refetchInterval: 15_000,
  })

  const onEvent = useCallback(
    (event: { type: string; payload?: Record<string, unknown> }) => {
      if (event.type === 'table.call_waiter') {
        const num = event.payload?.table_number
        toast.info(`🔔 ${t('admin.calls.calling', { table: String(num ?? '?') })}`)
        void queryClient.invalidateQueries({ queryKey: ['waiter-calls'] })
        return
      }
      if (event.type === 'waiter_call.attended') {
        void queryClient.invalidateQueries({ queryKey: ['waiter-calls'] })
        return
      }
      if (event.type === 'bill.updated') {
        void queryClient.invalidateQueries({ queryKey: ['bills'] })
        if (event.payload?.status === 'awaiting_payment') {
          toast.info(`🧾 ${t('admin.waiter.bills_title')}`)
        }
        return
      }
      if (event.type === 'bill.payment.updated') {
        // #163: uma parte foi paga online (ou reembolsada) — atualiza a conta
        void queryClient.invalidateQueries({ queryKey: ['bills'] })
        if (event.payload?.refunded) {
          toast.info(`↩️ ${t('admin.waiter.bill_refund_toast')}`)
        } else {
          toast.success(`💳 ${t('admin.waiter.bill_part_paid_toast')}`)
        }
        return
      }
      if (event.type === 'adjustment.decided') {
        // garçom: aviso da decisão do PRÓPRIO pedido de ajuste
        const req = event.payload?.request as
          { requested_by?: string; status?: string; decision_note?: string | null } | undefined
        if (req?.requested_by && req.requested_by === getTokenPayload()?.sub) {
          if (req.status === 'approved') toast.success(t('admin.adjust.your_approved_toast'))
          else if (req.status === 'rejected')
            toast.error(t('admin.adjust.your_rejected_toast', { note: req.decision_note ?? '' }))
        }
        void queryClient.invalidateQueries({ queryKey: ['orders'] })
        return
      }
      if (event.type === 'adjustment.requested') {
        if (getUserRoles().some((r) => isAdjustmentApprover(r))) {
          toast.info(
            t('admin.adjust.new_request_toast', {
              table: String(
                (event.payload?.request as { table_number?: number } | undefined)?.table_number ??
                  '?',
              ),
            }),
          )
        }
        return
      }
      if (event.type === 'order.ready') {
        toast.success(t('admin.orders.ready_toast'))
      }
      if (event.type === 'order.transferred') {
        const num = event.payload?.to_table_number
        toast.info(t('admin.orders.moved_toast', { table: String(num ?? '?') }))
      }
      if (event.type.startsWith('order.')) {
        void queryClient.invalidateQueries({ queryKey: ['orders'] })
      }
    },
    [queryClient, t],
  )
  const wsStatus = useRealtime({ role: 'waiter', onEvent })

  const billPayMutation = useMutation({
    mutationFn: (id: string) => billsApi.pay(id),
    onSuccess: () => {
      toast.success(t('admin.waiter.bill_paid_toast'))
      void queryClient.invalidateQueries({ queryKey: ['bills'] })
      void queryClient.invalidateQueries({ queryKey: ['orders'] })
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : t('admin.common.error')),
  })

  // #163: reembolso auditado de pagamento online — gestor (a rota reforsa)
  const billRefundMutation = useMutation({
    mutationFn: ({ billId, paymentId }: { billId: string; paymentId: string }) =>
      billsApi.refund(billId, { payment_id: paymentId }),
    onSuccess: (result) => {
      toast.success(t('admin.waiter.bill_refund_done', { id: result.stripe_refund_id }))
      void queryClient.invalidateQueries({ queryKey: ['bills'] })
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : t('admin.common.error')),
  })

  // Cancelar conta enviada — exclusivo da equipe (o cliente só cancela a
  // divisão 'open' antes de enviar; auditoria 0038 registra quem cancelou)
  const billCancelMutation = useMutation({
    mutationFn: (id: string) => billsApi.cancel(id),
    onSuccess: () => {
      toast.info(t('admin.waiter.bill_cancel_toast'))
      void queryClient.invalidateQueries({ queryKey: ['bills'] })
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : t('admin.common.error')),
  })

  const attendCallMutation = useMutation({
    mutationFn: (id: string) => waiterCallsApi.attend(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['waiter-calls'] }),
    onError: (err) => toast.error(err instanceof Error ? err.message : t('admin.calls.attend_err')),
  })

  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: OrderDetailed['status'] }) =>
      ordersApi.updateStatus(id, status),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['orders'] }),
    onError: (err) => toast.error(err instanceof Error ? err.message : t('admin.common.error')),
  })

  const payMutation = useMutation({
    mutationFn: (id: string) => ordersApi.pay(id),
    onSuccess: () => {
      toast.success(t('admin.orders.paid_toast'))
      void queryClient.invalidateQueries({ queryKey: ['orders'] })
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : t('admin.orders.paid_err')),
  })

  const reorderMutation = useMutation({
    mutationFn: (orderIds: string[]) => ordersApi.reorder(orderIds),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['orders'] }),
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : t('admin.orders.reorder_err')),
  })

  const boardOrders = useMemo(
    () =>
      (orders ?? []).filter((o) =>
        ['pending', 'confirmed', 'preparing', 'ready', 'delivered'].includes(o.status),
      ),
    [orders],
  )
  const toPayOrders = useMemo(
    () =>
      (orders ?? [])
        .filter((o) => o.status === 'delivered' && !o.paid_at)
        .sort((a, b) => a.created_at - b.created_at),
    [orders],
  )

  // "A pagar" é coluna derivada (entregues não pagos); pagar move o pedido
  // para "Entregues" — a fronteira entre elas é o pagamento, não o drag
  const waiterColumns: KanbanColumn[] = [
    { id: 'pending', status: 'pending', label: t('admin.waiter.col.pending') },
    { id: 'confirmed', status: 'confirmed', label: t('admin.waiter.col.confirmed') },
    { id: 'preparing', status: 'preparing', label: t('admin.waiter.col.preparing') },
    { id: 'ready', status: 'ready', label: t('admin.waiter.col.ready') },
    {
      id: 'to_pay',
      status: 'delivered',
      label: t('admin.waiter.col.to_pay'),
      match: (o) => o.status === 'delivered' && !o.paid_at,
      accent: 'bg-orange-100 text-orange-900 border-orange-300',
    },
  ]

  const countBy = (status: KanbanStatus) =>
    boardOrders.filter((o) =>
      status === 'delivered' ? o.status === 'delivered' && !o.paid_at : o.status === status,
    ).length

  const statusLabel = (st: KanbanStatus) => t(STATUS_KEY[st])

  const waiterCanMove = (order: OrderDetailed, target: KanbanStatus) => {
    if (!NEXT_STATUS[order.status].includes(target)) {
      toast.error(
        t('admin.orders.invalid_move', {
          from: statusLabel(order.status),
          to: statusLabel(target),
        }),
      )
      return false
    }
    if (!WAITER_TARGETS.includes(target)) {
      toast.error(t('admin.orders.kitchen_only'))
      return false
    }
    return true
  }

  const waiterMove = (order: OrderDetailed, target: KanbanStatus) =>
    statusMutation.mutate({ id: order.id, status: target })

  const waiterCard = (order: OrderDetailed) => (
    <div
      data-testid={`order-${order.id}`}
      className="rounded-xl border border-gray-200 bg-white shadow-sm flex flex-col cursor-grab active:cursor-grabbing overflow-hidden"
    >
      {/* Cronômetro + total — mesa e status já estão na coluna e no grupo */}
      <div className="flex items-center justify-between gap-2 px-3 py-1.5 border-b border-gray-100 min-w-0">
        <span className="text-xs font-bold tabular-nums whitespace-nowrap text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
          ⏱ {elapsedLabel(order.created_at)}
        </span>
        <span className="font-bold text-gray-900 text-sm whitespace-nowrap flex-shrink-0 tabular-nums">
          {formatCents(order.total_cents, currency, intlLocale)}
        </span>
      </div>

      {order.customer_name && (
        <p className="px-3 pt-1.5 text-xs text-gray-500 truncate">
          {t('admin.orders.customer')}: {order.customer_name}
        </p>
      )}

      <ul className="px-3 py-1.5 space-y-1 min-w-0">
        {order.items.map((item) => (
          <li key={item.id} className="text-sm text-gray-700 flex justify-between gap-2 min-w-0">
            <span className={cn('min-w-0 truncate', item.is_done && 'text-gray-400 line-through')}>
              {item.quantity}× {item.product_name}
              {item.variant_label && (
                <span className="font-semibold text-gray-500"> ({item.variant_label})</span>
              )}
              {item.removed_ingredients.length > 0 && (
                <span className="text-xs text-red-600">
                  {' '}
                  (sem {item.removed_ingredients.join(', ')})
                </span>
              )}
            </span>
            {item.is_done && (
              <span className="text-xs text-green-600">{t('admin.orders.done')}</span>
            )}
          </li>
        ))}
      </ul>

      <div className="px-3 py-1 border-t border-gray-100 text-xs text-gray-400">
        {t('admin.orders.entered_at')}:{' '}
        <span data-testid={`order-time-${order.id}`}>
          {formatInRestaurant(order.created_at * 1000, lang, restaurantTz)}
        </span>
      </div>

      <div className="px-3 py-2 border-t border-gray-100 flex flex-wrap gap-2">
        {['pending', 'confirmed'].includes(order.status) && (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              setEditingOrder(order)
              setFormOpen(true)
            }}
          >
            {t('admin.common.edit')}
          </Button>
        )}
        {order.status === 'pending' && (
          <Button
            size="sm"
            onClick={() => statusMutation.mutate({ id: order.id, status: 'confirmed' })}
          >
            {t('admin.orders.confirm')}
          </Button>
        )}
        {order.channel === 'dine_in' && TRANSFERABLE.includes(order.status) && !order.paid_at && (
          <Button size="sm" variant="secondary" onClick={() => setTransferOrder(order)}>
            {t('admin.orders.transfer')}
          </Button>
        )}
        {order.status !== 'cancelled' && !order.paid_at && (
          <Button
            size="sm"
            variant="secondary"
            data-testid={`order-adjust-${order.id}`}
            onClick={() => setAdjustOrder(order)}
          >
            {canApproveAdjustments
              ? t('admin.adjust.button_apply')
              : t('admin.adjust.button_request')}
          </Button>
        )}
        {order.status === 'ready' && (
          <Button
            size="sm"
            className="bg-green-600 hover:bg-green-700"
            onClick={() => statusMutation.mutate({ id: order.id, status: 'delivered' })}
          >
            {t('admin.orders.deliver')}
          </Button>
        )}
        {(order.status === 'ready' || order.status === 'delivered') && !order.paid_at && (
          <Button size="sm" variant="secondary" onClick={() => payMutation.mutate(order.id)}>
            {t('admin.orders.mark_paid')}
          </Button>
        )}
        {['pending', 'confirmed'].includes(order.status) && (
          <Button
            size="sm"
            variant="ghost"
            className="text-red-600 hover:bg-red-50"
            onClick={() => statusMutation.mutate({ id: order.id, status: 'cancelled' })}
          >
            {t('admin.orders.cancel')}
          </Button>
        )}
      </div>
    </div>
  )

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {waiterMode ? t('admin.waiter.flow_title') : t('admin.nav.waiter')}
          </h1>
          <p className="text-sm text-gray-500">
            {waiterMode ? `${t('admin.waiter.flow_title')} · ` : `${t('admin.waiter.hint')} · `}
            <span
              data-testid="waiter-realtime"
              data-status={wsStatus}
              className={cn(
                'font-medium',
                wsStatus === 'open' ? 'text-green-600' : 'text-amber-600',
              )}
            >
              {wsStatus === 'open' ? t('admin.waiter.rt_on') : t('admin.waiter.rt_off')}
            </span>
            {cfg && (
              <>
                {' · '}
                <span className={cfg.is_open ? 'text-green-600' : 'text-red-600'}>
                  {cfg.is_open ? t('admin.waiter.open') : t('admin.waiter.closed')}
                </span>
              </>
            )}
            {toPayOrders.length > 0 && (
              <>
                {' · '}
                <span className="font-medium text-orange-600">
                  {t('admin.waiter.to_pay', { count: String(toPayOrders.length) })}
                </span>
              </>
            )}
            {openCalls.length > 0 && (
              <>
                {' · '}
                <span className="font-medium text-red-600">
                  {t('admin.waiter.calling', { count: String(openCalls.length) })}
                </span>
              </>
            )}
          </p>
        </div>
        <Button
          onClick={() => {
            setEditingOrder(null)
            setFormOpen(true)
          }}
        >
          {t('admin.waiter.new_order')}
        </Button>
      </div>

      {/* #132: mesas atendidas — o garçom declara as suas e filtra o salão */}
      <WaiterTableClaims mineOnly={mineOnly} onMineOnlyChange={setMineOnly} />

      {/* Contas enviadas pelo cliente — aguardando confirmação de recebimento */}
      {(bills ?? []).length > 0 && (
        <div className="rounded-xl border border-emerald-300 bg-emerald-50 p-3 space-y-2">
          <p className="text-xs font-bold uppercase tracking-wide text-emerald-800">
            🧾 {t('admin.waiter.bills_title')}
          </p>
          {(bills ?? []).map((bill) => (
            <BillCard
              key={bill.id}
              bill={bill}
              currency={currency}
              priceLocale={intlLocale}
              onPay={billPayMutation.mutate}
              onCancel={billCancelMutation.mutate}
              onRefund={billRefundMutation.mutate}
              canRefund={hasAnyRole(getTokenPayload(), MANAGER_ROLES)}
            />
          ))}
        </div>
      )}

      {/* Fila de chamadas do salão — persistida; atender dá baixa na chamada */}
      {openCalls.length > 0 && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 space-y-2">
          {openCalls.map((call) => (
            <div
              key={call.id}
              className="flex items-center justify-between gap-3 rounded-lg bg-white border border-amber-200 px-3 py-2"
            >
              <div className="flex items-center gap-3 min-w-0">
                <span className="text-lg" aria-hidden>
                  🔔
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-bold text-gray-900 truncate">
                    {t('admin.orders.table')} {call.table_number}
                    {call.table_label ? ` · ${call.table_label}` : ''}
                  </p>
                  <p className="text-xs text-gray-500">
                    {t('admin.calls.waiting', { time: elapsedLabel(call.called_at) })}
                  </p>
                </div>
              </div>
              <Button
                size="sm"
                disabled={attendCallMutation.isPending}
                onClick={() => attendCallMutation.mutate(call.id)}
              >
                {t('admin.calls.attend')}
              </Button>
            </div>
          ))}
        </div>
      )}

      {waiterMode ? (
        /* Modo garçom (0085): pedidos da mesa enviados pelo cliente, por mesa —
           nome de quem pediu, extras e removidos; termina no sistema local. */
        <WaiterTables
          lang={lang}
          priceLocale={intlLocale}
          currency={currency}
          mineOnly={mineOnly}
        />
      ) : isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner className="w-6 h-6 text-indigo-500" />
        </div>
      ) : boardOrders.length === 0 ? (
        <Empty title={t('admin.waiter.empty')} description={t('admin.waiter.empty_hint')} />
      ) : (
        <>
          {/* celular: abas por status — o garçom opera com uma mão */}
          <div className="lg:hidden flex gap-1 overflow-x-auto mb-3 pb-1">
            {waiterColumns.map((col) => (
              <button
                key={col.id}
                onClick={() => setMobileTab(col.status)}
                className={cn(
                  'flex items-center gap-1.5 px-3 py-2 rounded-lg border text-xs font-bold whitespace-nowrap transition-colors',
                  mobileTab === col.status
                    ? 'border-indigo-600 bg-indigo-600 text-white'
                    : 'border-gray-200 bg-white text-gray-600',
                )}
              >
                {col.id === 'to_pay' ? t('admin.waiter.col.to_pay') : col.label}
                <span
                  className={cn(
                    'rounded-full px-1.5 py-0.5 text-[10px]',
                    mobileTab === col.status ? 'bg-white/25' : 'bg-gray-100',
                  )}
                >
                  {countBy(col.status)}
                </span>
              </button>
            ))}
          </div>

          <div className="lg:hidden">
            <OrdersKanban
              orders={boardOrders}
              columns={waiterColumns.filter((c) => c.status === mobileTab)}
              canMove={waiterCanMove}
              onMove={waiterMove}
              onReorder={(_status: KanbanStatus, ids: string[]) => reorderMutation.mutate(ids)}
              groupKey={(o) =>
                o.channel === 'dine_in'
                  ? `${t('admin.orders.table')} ${o.table_label ?? o.table_number ?? '?'}`
                  : o.channel === 'takeaway'
                    ? t('admin.channel.takeaway')
                    : t('admin.channel.delivery')
              }
              subgroupKey={(o) => o.reference_name}
              renderCard={waiterCard}
            />
          </div>

          <div className="hidden lg:block">
            <OrdersKanban
              orders={boardOrders}
              columns={waiterColumns}
              canMove={waiterCanMove}
              onMove={waiterMove}
              onReorder={(_status: KanbanStatus, ids: string[]) => reorderMutation.mutate(ids)}
              groupKey={(o) =>
                o.channel === 'dine_in'
                  ? `${t('admin.orders.table')} ${o.table_label ?? o.table_number ?? '?'}`
                  : o.channel === 'takeaway'
                    ? t('admin.channel.takeaway')
                    : t('admin.channel.delivery')
              }
              subgroupKey={(o) => o.reference_name}
              renderCard={waiterCard}
            />
          </div>
        </>
      )}

      <OrderFormModal
        open={formOpen}
        order={editingOrder}
        onClose={() => setFormOpen(false)}
        onSaved={() => void queryClient.invalidateQueries({ queryKey: ['orders'] })}
      />

      <PriceAdjustmentModal
        order={adjustOrder}
        canApprove={canApproveAdjustments}
        onClose={() => setAdjustOrder(null)}
        onSaved={() => void queryClient.invalidateQueries({ queryKey: ['orders'] })}
        formatMoney={(c) => formatCents(c, currency, intlLocale)}
      />

      <TransferTableModal
        order={transferOrder}
        onClose={() => setTransferOrder(null)}
        onSaved={() => void queryClient.invalidateQueries({ queryKey: ['orders'] })}
      />
    </div>
  )
}

// ─── Contas a receber — card de conta enviada pelo cliente ───────────────────

function BillCard({
  bill,
  currency,
  priceLocale = 'pt',
  onPay,
  onCancel,
  onRefund,
  canRefund,
}: {
  bill: BillDto
  /** moeda do país do tenant (#419/#413) */
  currency: string
  /** #428: locale de formatação Intl (língua base + país) */
  priceLocale?: string
  onPay: (id: string) => void
  onCancel: (id: string) => void
  onRefund: (input: { billId: string; paymentId: string }) => void
  canRefund: boolean
}) {
  const { t } = useI18n()
  const modeLabel =
    bill.mode === 'single'
      ? t('admin.waiter.bill_mode_single')
      : bill.mode === 'by_people'
        ? t('admin.waiter.bill_mode_by_people')
        : t('admin.waiter.bill_mode_flexible')
  const methodLabel = (m: string | null | undefined) =>
    m === 'cash'
      ? t('admin.branding.pay_cash')
      : m === 'card_local'
        ? t('admin.branding.pay_card')
        : m === 'online'
          ? t('admin.waiter.pay_online')
          : '—'
  const paidOnline = bill.online_paid_cents ?? 0
  const pendingOnline = bill.online_pending_cents ?? 0
  const due = bill.remaining_due_cents ?? bill.total_cents

  return (
    <div
      data-testid={`bill-${bill.id}`}
      className="rounded-lg bg-white border border-emerald-200 px-3 py-2 space-y-2"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-bold text-gray-900">
            {t('admin.orders.table')} {bill.table_number}
            {bill.table_label ? ` · ${bill.table_label}` : ''}
            <span className="ml-2 text-xs font-medium text-gray-500">{modeLabel}</span>
          </p>
          <p className="text-xs text-gray-500">
            {bill.order_count} pedido(s)
            {bill.awaiting_since
              ? ` · ${t('admin.calls.waiting', { time: elapsedLabel(bill.awaiting_since) })}`
              : ''}
          </p>
        </div>
        <p className="text-sm font-extrabold text-gray-900 shrink-0">
          {formatCents(bill.total_cents, currency, priceLocale)}
        </p>
      </div>

      {/* como vão pagar: cota por pessoa (by_people/flexible) ou método único */}
      <div className="space-y-1">
        {bill.mode === 'single' && (
          <p className="text-xs text-gray-600">💵 {methodLabel(bill.payment_methods[0])}</p>
        )}
        {bill.mode === 'by_people' &&
          (bill.shares ?? []).map((share, i) => {
            const slotPaid = (bill.online_payments ?? []).some(
              (pay) => pay.slot === i && pay.status === 'succeeded',
            )
            return (
              <p key={i} className="text-xs text-gray-600">
                👤 #{i + 1} · {methodLabel(bill.payment_methods[i])} ·{' '}
                {formatCents(share, currency, priceLocale)}
                {slotPaid ? ` · ✅ ${t('admin.waiter.paid_online_badge')}` : ''}
              </p>
            )
          })}
        {bill.mode === 'flexible' &&
          (bill.participants ?? []).map((p) => (
            <p key={p.id} className="text-xs text-gray-600">
              {p.kind === 'fixed' ? '💶' : '🍽️'} {p.display_name ?? '—'} ·{' '}
              {methodLabel(p.payment_method)} · {formatCents(p.share_cents, currency, priceLocale)}
              {p.kind === 'items' && p.divide_by > 1 ? ` (÷${p.divide_by})` : ''}
              {(p.online_paid_cents ?? 0) >= p.share_cents && p.share_cents > 0
                ? ` · ✅ ${t('admin.waiter.paid_online_badge')}`
                : (p.online_paid_cents ?? 0) > 0
                  ? ` · 💳 ${formatCents(p.online_paid_cents, currency, priceLocale)}`
                  : ''}
            </p>
          ))}
        {bill.tip_cents > 0 && (
          <p className="text-xs text-gray-500">
            {t('admin.waiter.bill_tip')}: {formatCents(bill.tip_cents, currency, priceLocale)}
          </p>
        )}
      </div>

      {/* #163: resumo do que já veio online e do que falta receber */}
      {bill.online_payments?.length > 0 && (
        <div data-testid={`bill-online-${bill.id}`} className="space-y-1">
          {bill.online_payments.map((p) => (
            <p key={p.id} className="text-xs text-gray-600 flex items-center gap-1 flex-wrap">
              💳 {formatCents(p.amount_cents, currency, priceLocale)}
              {p.tip_cents > 0
                ? ` (+${formatCents(p.tip_cents, currency, priceLocale)} ${t('admin.waiter.bill_tip')})`
                : ''}
              {' · '}
              {t(`admin.waiter.payment_${p.status}`)}
              {p.refunded_cents > 0 && (
                <span className="text-amber-700">
                  {' · '}
                  {t('admin.waiter.payment_refunded', {
                    amount: formatCents(p.refunded_cents, currency, priceLocale),
                  })}
                </span>
              )}
              {canRefund && p.status === 'succeeded' && p.refunded_cents < p.amount_cents && (
                <button
                  type="button"
                  data-testid={`bill-refund-${p.id}`}
                  className="text-red-600 underline underline-offset-2"
                  onClick={() => {
                    if (
                      window.confirm(
                        `${t('admin.waiter.bill_refund_confirm', {
                          amount: formatCents(
                            p.amount_cents - p.refunded_cents,
                            currency,
                            priceLocale,
                          ),
                        })}?`,
                      )
                    )
                      onRefund({ billId: bill.id, paymentId: p.id })
                  }}
                >
                  {t('admin.waiter.bill_refund')}
                </button>
              )}
            </p>
          ))}
          <p
            className="text-xs font-bold text-emerald-700"
            data-testid={`bill-online-sum-${bill.id}`}
          >
            {t('admin.waiter.online_paid')}: {formatCents(paidOnline, currency, priceLocale)}
            {pendingOnline > 0 &&
              ` · ${t('admin.waiter.online_processing')}: ${formatCents(pendingOnline, currency, priceLocale)}`}
            {' · '}
            {t('admin.waiter.online_due')}: {formatCents(due, currency, priceLocale)}
          </p>
        </div>
      )}

      <div className="flex gap-2">
        {due > 0 && (
          <Button size="sm" className="flex-1" onClick={() => onPay(bill.id)}>
            ✅ {t('admin.waiter.bill_confirm')}
          </Button>
        )}
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            if (window.confirm(t('admin.waiter.bill_cancel') + '?')) onCancel(bill.id)
          }}
        >
          ✕ {t('admin.waiter.bill_cancel')}
        </Button>
      </div>
    </div>
  )
}
